import { useEffect, useRef, useState as useStateRaw, type ReactNode } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { useLanguageContext } from '@/i18n/LanguageContext';
import Seo from '@/components/seo/Seo';
import { overseasSchools, overseasRegions } from '@/mocks/overseasSchools';

import { imagesFor } from '@/mocks/overseasImages';
function FadeInSection({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useStateRaw(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`fade-in-section ${isVisible ? 'is-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

export default function OverseasSchoolsPage() {
  const { lang } = useLanguageContext();
  const href = (path: string) => (lang === 'en' ? (path === '/' ? '/en' : `/en${path}`) : path);

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: lang === 'en' ? 'Home' : 'ホーム', item: 'https://butler-academy.jp/' },
      {
        '@type': 'ListItem',
        position: 2,
        name: lang === 'en' ? 'Understanding Butlers' : '執事を知る',
        item: 'https://butler-academy.jp/overseas-schools',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: lang === 'en' ? 'Butler Schools Around the World' : '海外の執事学校',
        item: 'https://butler-academy.jp/overseas-schools',
      },
    ],
  };

  const webPageStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: lang === 'en' ? 'Butler Schools Around the World' : '海外の執事学校',
    description:
      lang === 'en'
        ? 'Butler education institutions span not only the UK but also Europe, North America, Asia, the Middle East, and Oceania. We organize only facts published on each school\'s official website, with sources.'
        : '執事の教育機関は、英国だけでなくヨーロッパ・北米・アジア・中東・オセアニアに広がっています。各校が公式サイトで公表している事実のみを、出典とともに整理しました。',
    url: 'https://butler-academy.jp/overseas-schools',
    isPartOf: {
      '@type': 'WebSite',
      name: lang === 'en' ? 'Japan Butler Academy' : '日本執事アカデミー',
      url: 'https://butler-academy.jp/',
    },
  };

  return (
    <>
      <Header />
      <main>
        <Seo route="/overseas-schools" jsonLd={[breadcrumbStructuredData, webPageStructuredData]} />

        {/* Hero */}
        <section className="hero-billboard relative w-full h-[60vh] min-h-[460px] md:h-[82vh] md:min-h-[640px] overflow-hidden">
          <img
            src="/images/generated/philosophy-hero-2026.jpg"
            alt={lang === 'en' ? 'Butler Schools Around the World' : '海外の執事学校'}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 hero-overlay"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="relative w-full px-8">
              <div className="max-w-[800px] mx-auto text-center">
                <div className="flex items-center justify-center gap-2 mb-8 text-[14px] tracking-[0.05em]">
                  <Link to={href('/')} className="text-foreground-200/70 hover:text-accent-400 transition-colors">
                    {lang === 'en' ? 'Home' : 'ホーム'}
                  </Link>
                  <i className="ri-arrow-right-s-line text-foreground-400/60 text-[12px]"></i>
                  <span className="text-foreground-200/70">
                    {lang === 'en' ? 'Understanding Butlers' : '執事を知る'}
                  </span>
                  <i className="ri-arrow-right-s-line text-foreground-400/60 text-[12px]"></i>
                  <span className="text-accent-400">
                    {lang === 'en' ? 'Butler Schools Around the World' : '海外の執事学校'}
                  </span>
                </div>
                <h1 className="font-heading text-2xl md:text-4xl text-foreground-50 tracking-[0.08em] mb-4">
                  海外の執事学校
                </h1>
                <p className="section-title-en text-accent-400 text-[15px] md:text-[17px] tracking-[0.12em] mb-6">
                  BUTLER SCHOOLS AROUND THE WORLD
                </p>
                <p className="text-[15px] md:text-[16px] text-foreground-200/80 tracking-[0.05em] leading-relaxed max-w-[620px] mx-auto">
                  執事の教育機関は、英国だけでなくヨーロッパ・北米・アジア・中東・オセアニアに広がっています。各校が公式サイトで公表している事実のみを、出典とともに整理しました。
                </p>
              </div>
            </div>
          </div>
        </section>

        <Breadcrumb
          items={[
            { label: '執事を知る', labelEn: 'Understanding Butlers' },
            { label: '海外の執事学校', labelEn: 'Butler Schools Around the World' },
          ]}
        />

        {/* Schools by region */}
        {overseasRegions.map((region) => {
          const schools = overseasSchools.filter((s) => s.region === region);
          if (schools.length === 0) return null;
          return (
            <FadeInSection key={region}>
              <section className="relative py-20 md:py-28 bg-background-50 odd:bg-background-100/50">
                <div className="max-w-[1200px] mx-auto px-8">
                  <div className="text-center mb-14">
                    <p className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">REGION</p>
                    <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
                      {region}
                    </h2>
                    <div className="gold-line mx-auto"></div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {schools.map((school) => {
                      const thumb = imagesFor(school.slug)[0];
                      return (
                      <div
                        key={school.slug}
                        className="card-glow group flex flex-col bg-background-50 border border-background-200/60 overflow-hidden"
                      >
                        {thumb && (
                          <figure className="mb-0">
                            <img src={thumb.file} alt={school.nameJa} loading="lazy" className="w-full h-[170px] object-cover object-center bg-background-100" />
                            <figcaption className="px-6 pt-2 text-[11px] text-foreground-400 leading-relaxed">出典: {school.nameJa} 公式サイト</figcaption>
                          </figure>
                        )}
                        <div className="flex flex-col flex-1 p-6">
                          <h3 className="font-heading text-[17px] text-foreground-900 tracking-[0.05em] mb-2 group-hover:text-accent-600 transition-colors">
                            {school.nameJa}
                          </h3>
                          <p className="section-title-en text-accent-500 text-[13px] tracking-[0.08em] mb-4">
                            {school.name}
                          </p>
                          <dl className="text-[14px] text-foreground-500 tracking-[0.03em] leading-relaxed mb-4 space-y-2 flex-1">
                            <div className="flex gap-2">
                              <dt className="text-foreground-400 shrink-0">所在地</dt>
                              <dd>
                                {school.country} / {school.city}
                              </dd>
                            </div>
                            <div className="flex gap-2">
                              <dt className="text-foreground-400 shrink-0">設立</dt>
                              <dd>{school.founded}</dd>
                            </div>
                            {school.courses.length > 0 && (
                              <div className="flex gap-2">
                                <dt className="text-foreground-400 shrink-0">課程</dt>
                                <dd>
                                  <ul className="list-disc list-inside space-y-1">
                                    {school.courses.slice(0, 2).map((course) => (
                                      <li key={course}>{course}</li>
                                    ))}
                                  </ul>
                                </dd>
                              </div>
                            )}
                          </dl>
                          <div className="pt-4 border-t border-background-200/60">
                            <Link
                              to={href(`/overseas-schools/${school.slug}`)}
                              className="flex items-center gap-2 text-accent-600 hover:text-accent-500 transition-colors"
                            >
                              <span className="text-[14px] tracking-[0.05em]">詳しく見る</span>
                              <i className="ri-arrow-right-line text-xs"></i>
                            </Link>
                          </div>
                        </div>
                      </div>
                    );
                    })}
                  </div>
                </div>
              </section>
            </FadeInSection>
          );
        })}

        {/* Source note */}
        <FadeInSection>
          <section className="relative py-16 md:py-20 bg-background-100/50">
            <div className="max-w-[860px] mx-auto px-8 text-center">
              <p className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">SOURCE</p>
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
                出典について
              </h2>
              <div className="gold-line mx-auto mb-8"></div>
              <p className="text-[15px] md:text-[16px] text-foreground-600 tracking-[0.03em] leading-loose text-left">
                掲載内容は各校の公式サイトの記載に基づきます。取得日は各校の詳細ページに記載しています。公式サイトに記載がない項目は「記載なし」と明示しています。
              </p>
            </div>
          </section>
        </FadeInSection>

        {/* Japan Butler Academy */}
        <FadeInSection>
          <section className="relative py-20 md:py-28 bg-primary-900 text-foreground-200">
            <div className="max-w-[820px] mx-auto px-8 text-center">
              <p className="text-accent-400 text-[13px] tracking-[0.2em] mb-5 font-label">JAPAN</p>
              <h2 className="font-heading text-xl md:text-2xl text-foreground-50 tracking-[0.06em] mb-2">
                日本執事アカデミーの位置づけ
              </h2>
              <p className="section-title-en text-accent-400 text-[15px] tracking-[0.12em] mb-6">
                JAPAN BUTLER ACADEMY
              </p>
              <div className="gold-line mx-auto mb-8"></div>
              <p className="text-[15px] md:text-[16px] text-foreground-200/80 tracking-[0.03em] leading-loose mb-8">
                日本執事アカデミーは、日本で唯一の学び舎を持つ執事養成学校です。東京・お台場の本校に実際に通えます。
              </p>
              <Link
                to={href('/japan-butler-academy')}
                className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-400 text-primary-900 text-[15px] tracking-[0.05em] px-8 py-3 transition-colors"
              >
                日本執事アカデミーを見る
                <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>
          </section>
        </FadeInSection>

        {/* Related links */}
        <FadeInSection>
          <section className="relative py-20 md:py-24 bg-background-50">
            <div className="max-w-[760px] mx-auto px-8 text-center">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-4">
                関連リンク
              </h2>
              <div className="gold-line mx-auto mb-8"></div>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <Link
                  to={href('/butler-school')}
                  className="inline-flex items-center gap-2 border border-accent-500/50 hover:border-accent-400 text-accent-600 text-[15px] tracking-[0.05em] px-8 py-3 transition-colors"
                >
                  執事学校とは？
                </Link>
                <Link
                  to={href('/butler-training-school')}
                  className="inline-flex items-center gap-2 border border-accent-500/50 hover:border-accent-400 text-accent-600 text-[15px] tracking-[0.05em] px-8 py-3 transition-colors"
                >
                  執事養成学校とは？
                </Link>
                <Link
                  to={href('/butler-job')}
                  className="inline-flex items-center gap-2 border border-accent-500/50 hover:border-accent-400 text-accent-600 text-[15px] tracking-[0.05em] px-8 py-3 transition-colors"
                >
                  執事の仕事とは？
                </Link>
              </div>
            </div>
          </section>
        </FadeInSection>

        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 w-11 h-11 rounded-full border border-accent-500/40 text-accent-500 hover:bg-accent-500/10 hover:border-accent-400 transition-all duration-300 flex items-center justify-center cursor-pointer z-40"
          aria-label={lang === 'en' ? 'Back to top' : 'ページ上部へ'}
        >
          <i className="ri-arrow-up-line"></i>
        </button>
      </main>
      <Footer />
    </>
  );
}
